/**
 * @fileoverview Lifecycle reason fields.
 * @description The reason picker and note box shared by the Deactivate and Reactivate dialogs: the
 * reason is one of the configuration's closed list (each labelled in the user's language when the
 * app has a translation for its code, and by the backend's own label otherwise), and the note is
 * optional except for the reason OTHER, where it is required. The note's length limit comes from
 * the configuration. The dialogs own the form; this only renders the two fields they register.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import Label from '@/shared/components/atoms/Label'
import SelectField from '@/shared/components/molecules/SelectField'

import type { LifecycleReason } from '@/features/users/types/users.types'
import type { UseFormRegisterReturn } from 'react-hook-form'

interface LifecycleReasonFieldsProps {
  /** Prefixes the fields' ids and test ids, e.g. "deactivateUser". */
  idPrefix: string
  /** The dialog's test id prefix, e.g. "deactivate-user". */
  testIdPrefix: string
  reasons: LifecycleReason[]
  reasonRegistration: UseFormRegisterReturn
  noteRegistration: UseFormRegisterReturn
  /** Whether the chosen reason needs a note. */
  noteRequired: boolean
  noteMaxLength: number
  reasonError?: string | undefined
  noteError?: string | undefined
}

/**
 * @component
 * @param {LifecycleReasonFieldsProps} props - The reasons, the two registered fields and their
 * errors.
 * @param {string} props.idPrefix - Prefixes the fields' ids.
 * @param {string} props.testIdPrefix - The dialog's test id prefix.
 * @param {LifecycleReason[]} props.reasons - The reasons to choose from.
 * @param {UseFormRegisterReturn} props.reasonRegistration - The reason select's registration.
 * @param {UseFormRegisterReturn} props.noteRegistration - The note box's registration.
 * @param {boolean} props.noteRequired - Whether the chosen reason needs a note.
 * @param {number} props.noteMaxLength - The longest the note may be.
 * @param {string} [props.reasonError] - The reason's validation message.
 * @param {string} [props.noteError] - The note's validation message.
 */
function LifecycleReasonFields({
  idPrefix,
  testIdPrefix,
  reasons,
  reasonRegistration,
  noteRegistration,
  noteRequired,
  noteMaxLength,
  reasonError,
  noteError,
}: LifecycleReasonFieldsProps) {
  const { t } = useTranslation()
  const noteLabel = t(
    noteRequired
      ? 'sales.userManagement.lifecycle.noteLabelRequired'
      : 'sales.userManagement.lifecycle.noteLabel',
  )

  return (
    <>
      <SelectField
        id={`${idPrefix}Reason`}
        data-testid={`${testIdPrefix}-reason-select`}
        label={`${t('sales.userManagement.lifecycle.reasonLabel')} *`}
        options={[
          { value: '', label: t('sales.userManagement.lifecycle.reasonPlaceholder') },
          ...reasons.map((reason) => ({
            value: reason.code,
            label: t(`sales.userManagement.lifecycle.reasons.${reason.code}`, {
              defaultValue: reason.label,
            }),
          })),
        ]}
        error={reasonError}
        {...reasonRegistration}
      />
      <div>
        <Label htmlFor={`${idPrefix}Note`}>{noteLabel}</Label>
        <textarea
          id={`${idPrefix}Note`}
          data-testid={`${testIdPrefix}-note-input`}
          rows={3}
          maxLength={noteMaxLength}
          placeholder={t('sales.userManagement.lifecycle.notePlaceholder')}
          aria-invalid={Boolean(noteError)}
          className="mt-2 w-full rounded-lg border border-input-border bg-white px-3 py-2 text-[13.5px] text-ink focus:ring-2 focus:ring-primary/20 focus:outline-none"
          {...noteRegistration}
        />
        {noteError && (
          <p className="mt-1.5 text-[13px] text-red-600" data-testid={`${testIdPrefix}-note-error`}>
            {noteError}
          </p>
        )}
      </div>
    </>
  )
}

export default LifecycleReasonFields
